import { SBOX, INV_SBOX, MIX, IMix } from './aesTables';
import { base64ToBytes, bytesToBase64, concatBytes, randomBytes, utf8ToBytes, bytesToUtf8 } from './bytes';
import { md5Bytes } from './md5Core';

function evpBytesToKey(password: Uint8Array, salt: Uint8Array, keyLen: number, ivLen: number): {
  key: Uint8Array;
  iv: Uint8Array;
} {
  const target = keyLen + ivLen;
  let derived = new Uint8Array(0);
  let block = new Uint8Array(0);
  while (derived.length < target) {
    block = md5Bytes(concatBytes(block, password, salt));
    derived = concatBytes(derived, block);
  }
  return {
    key: derived.slice(0, keyLen),
    iv: derived.slice(keyLen, keyLen + ivLen),
  };
}

function subWord(word: number): number {
  return (
    (SBOX[(word >>> 24) & 0xff] << 24) |
    (SBOX[(word >>> 16) & 0xff] << 16) |
    (SBOX[(word >>> 8) & 0xff] << 8) |
    SBOX[word & 0xff]
  ) >>> 0;
}

function rotWord(word: number): number {
  return ((word << 8) | (word >>> 24)) >>> 0;
}

function expandKey(key: Uint8Array): Uint32Array {
  const Nk = key.length / 4;
  const Nr = Nk + 6;
  const w = new Uint32Array(4 * (Nr + 1));
  for (let i = 0; i < Nk; i += 1) {
    w[i] =
      ((key[4 * i] << 24) | (key[4 * i + 1] << 16) | (key[4 * i + 2] << 8) | key[4 * i + 3]) >>> 0;
  }
  const rcon = [0x00, 0x01, 0x02, 0x04, 0x08, 0x10, 0x20, 0x40, 0x80, 0x1b, 0x36];
  for (let i = Nk; i < w.length; i += 1) {
    let temp = w[i - 1];
    if (i % Nk === 0) {
      temp = (subWord(rotWord(temp)) ^ (rcon[i / Nk] << 24)) >>> 0;
    } else if (Nk > 6 && i % Nk === 4) {
      temp = subWord(temp);
    }
    w[i] = (w[i - Nk] ^ temp) >>> 0;
  }
  return w;
}

function addRoundKey(state: Uint8Array, roundKey: Uint32Array, round: number) {
  for (let c = 0; c < 4; c += 1) {
    const k = roundKey[round * 4 + c];
    state[c * 4] ^= (k >>> 24) & 0xff;
    state[c * 4 + 1] ^= (k >>> 16) & 0xff;
    state[c * 4 + 2] ^= (k >>> 8) & 0xff;
    state[c * 4 + 3] ^= k & 0xff;
  }
}

function encryptBlock(input: Uint8Array, roundKey: Uint32Array): Uint8Array {
  const Nr = roundKey.length / 4 - 1;
  const state = new Uint8Array(16);
  for (let i = 0; i < 16; i += 1) state[i] = input[i];
  addRoundKey(state, roundKey, 0);
  for (let round = 1; round < Nr; round += 1) {
    for (let i = 0; i < 16; i += 1) state[i] = SBOX[state[i]];
    const t = new Uint8Array(state);
    state[1] = t[5];
    state[5] = t[9];
    state[9] = t[13];
    state[13] = t[1];
    state[2] = t[10];
    state[6] = t[14];
    state[10] = t[2];
    state[14] = t[6];
    state[3] = t[15];
    state[7] = t[3];
    state[11] = t[7];
    state[15] = t[11];
    for (let c = 0; c < 4; c += 1) {
      const i = c * 4;
      const s0 = state[i];
      const s1 = state[i + 1];
      const s2 = state[i + 2];
      const s3 = state[i + 3];
      state[i] = MIX[0][s0] ^ MIX[1][s1] ^ MIX[2][s2] ^ MIX[3][s3];
      state[i + 1] = MIX[0][s1] ^ MIX[1][s2] ^ MIX[2][s3] ^ MIX[3][s0];
      state[i + 2] = MIX[0][s2] ^ MIX[1][s3] ^ MIX[2][s0] ^ MIX[3][s1];
      state[i + 3] = MIX[0][s3] ^ MIX[1][s0] ^ MIX[2][s1] ^ MIX[3][s2];
    }
    addRoundKey(state, roundKey, round);
  }
  for (let i = 0; i < 16; i += 1) state[i] = SBOX[state[i]];
  const t = new Uint8Array(state);
  state[1] = t[5];
  state[5] = t[9];
  state[9] = t[13];
  state[13] = t[1];
  state[2] = t[10];
  state[6] = t[14];
  state[10] = t[2];
  state[14] = t[6];
  state[3] = t[15];
  state[7] = t[3];
  state[11] = t[7];
  state[15] = t[11];
  addRoundKey(state, roundKey, Nr);
  return state;
}

function decryptBlock(input: Uint8Array, roundKey: Uint32Array): Uint8Array {
  const Nr = roundKey.length / 4 - 1;
  const state = new Uint8Array(16);
  for (let i = 0; i < 16; i += 1) state[i] = input[i];
  addRoundKey(state, roundKey, Nr);
  for (let round = Nr - 1; round >= 1; round -= 1) {
    const t = new Uint8Array(state);
    state[1] = t[13];
    state[5] = t[1];
    state[9] = t[5];
    state[13] = t[9];
    state[2] = t[10];
    state[6] = t[14];
    state[10] = t[2];
    state[14] = t[6];
    state[3] = t[7];
    state[7] = t[11];
    state[11] = t[15];
    state[15] = t[3];
    for (let i = 0; i < 16; i += 1) state[i] = INV_SBOX[state[i]];
    addRoundKey(state, roundKey, round);
    for (let c = 0; c < 4; c += 1) {
      const i = c * 4;
      const s0 = state[i];
      const s1 = state[i + 1];
      const s2 = state[i + 2];
      const s3 = state[i + 3];
      state[i] = IMix[0][s0] ^ IMix[1][s1] ^ IMix[2][s2] ^ IMix[3][s3];
      state[i + 1] = IMix[0][s1] ^ IMix[1][s2] ^ IMix[2][s3] ^ IMix[3][s0];
      state[i + 2] = IMix[0][s2] ^ IMix[1][s3] ^ IMix[2][s0] ^ IMix[3][s1];
      state[i + 3] = IMix[0][s3] ^ IMix[1][s0] ^ IMix[2][s1] ^ IMix[3][s2];
    }
  }
  const t = new Uint8Array(state);
  state[1] = t[13];
  state[5] = t[1];
  state[9] = t[5];
  state[13] = t[9];
  state[2] = t[10];
  state[6] = t[14];
  state[10] = t[2];
  state[14] = t[6];
  state[3] = t[7];
  state[7] = t[11];
  state[11] = t[15];
  state[15] = t[3];
  for (let i = 0; i < 16; i += 1) state[i] = INV_SBOX[state[i]];
  addRoundKey(state, roundKey, 0);
  return state;
}

function pkcs7Pad(data: Uint8Array): Uint8Array {
  const pad = 16 - (data.length % 16);
  const out = new Uint8Array(data.length + pad);
  out.set(data);
  out.fill(pad, data.length);
  return out;
}

function pkcs7Unpad(data: Uint8Array): Uint8Array {
  if (!data.length) return data;
  const pad = data[data.length - 1];
  if (pad < 1 || pad > 16 || pad > data.length) return data;
  return data.slice(0, data.length - pad);
}

function xorBlock(a: Uint8Array, b: Uint8Array): Uint8Array {
  const out = new Uint8Array(16);
  for (let i = 0; i < 16; i += 1) out[i] = a[i] ^ b[i];
  return out;
}

function aesCbcEncrypt(plain: Uint8Array, key: Uint8Array, iv: Uint8Array): Uint8Array {
  const roundKey = expandKey(key);
  const padded = pkcs7Pad(plain);
  const out = new Uint8Array(padded.length);
  let prev = iv;
  for (let i = 0; i < padded.length; i += 16) {
    const block = xorBlock(padded.slice(i, i + 16), prev);
    const enc = encryptBlock(block, roundKey);
    out.set(enc, i);
    prev = enc;
  }
  return out;
}

function aesCbcDecrypt(cipher: Uint8Array, key: Uint8Array, iv: Uint8Array): Uint8Array {
  const roundKey = expandKey(key);
  const out = new Uint8Array(cipher.length);
  let prev = iv;
  for (let i = 0; i < cipher.length; i += 16) {
    const block = cipher.slice(i, i + 16);
    const dec = xorBlock(decryptBlock(block, roundKey), prev);
    out.set(dec, i);
    prev = block;
  }
  return pkcs7Unpad(out);
}

const SALTED = utf8ToBytes('Salted__');

export function encryptOpenSslAes(plainText: string, passphrase: string): string {
  const salt = randomBytes(8);
  const { key, iv } = evpBytesToKey(utf8ToBytes(passphrase), salt, 32, 16);
  const cipher = aesCbcEncrypt(utf8ToBytes(plainText), key, iv);
  return bytesToBase64(concatBytes(SALTED, salt, cipher));
}

export function decryptOpenSslAes(cipherText: string, passphrase: string): string {
  if (!cipherText) return '';
  const raw = base64ToBytes(cipherText);
  if (raw.length < 16) return '';
  const magic = bytesToUtf8(raw.slice(0, 8));
  if (magic !== 'Salted__') return '';
  const salt = raw.slice(8, 16);
  const cipher = raw.slice(16);
  const { key, iv } = evpBytesToKey(utf8ToBytes(passphrase), salt, 32, 16);
  return bytesToUtf8(aesCbcDecrypt(cipher, key, iv));
}
